import { authenticateViaDialog, clearToken, isOfficeEnvironment, setToken } from "@/lib/office";

/**
 * Response interceptor that handles 401 Unauthorized responses.
 *
 * - **Office context**: Opens the login URL in an Office dialog, retrieves a
 *   JWT token, and retries the original request with a Bearer header.
 * - **Browser context**: Redirects the browser to the login/refresh URI.
 */
export async function authResponseInterceptor(
  response: Response,
  request: Request,
  _options: unknown,
): Promise<Response> {
  if (response.status !== 401) {
    return response;
  }

  const clonedResponse = response.clone();

  let redirectUri: string | undefined;
  try {
    const body = await clonedResponse.json();
    redirectUri = body.refreshUri ?? body.loginUri;
    console.log("Received 401 response, redirect URI:", redirectUri);
  } catch {
    console.error("Failed to parse 401 response body as JSON");
    return response;
  }

  if (!redirectUri) {
    return response;
  }

  if (isOfficeEnvironment()) {
    clearToken();

    const token = await authenticateViaDialog(redirectUri);
    if (token) {
      setToken(token);

      // Retry the original request with the new Bearer token
      const retryHeaders = new Headers(request.headers);
      retryHeaders.set("Authorization", `Bearer ${token}`);
      return fetch(new Request(request, { headers: retryHeaders }));
    }

    return response;
  }

  console.log("Redirecting browser to login/refresh URI:", redirectUri);
  // Non-Office: redirect for re-authentication
  window.location.href = redirectUri;
  return response;
}
